import { Add, Delete, Edit, RemoveRedEye } from "@mui/icons-material";
import BlindsClosedIcon from "@mui/icons-material/BlindsClosed";
import {
  Button,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TableCell,
  Tooltip,
  Typography,
} from "@mui/material";
import EmptyContent from "components/EmptyContent";
import List from "components/UI/List";
import { useAlert } from "contexts/alertContext";
import { Feature } from "models/api";
import { IWorkshopsGroup, IWorkshopsGroupInput } from "models/workshopsgroup";
import React, { useCallback, useEffect, useState } from "react";
import { QueryClient, useMutation } from "react-query";

import {
  WorkshopCells,
  addWorkshopsGroup,
  deleteWorkshopsGroup,
  editWorkshopsGroup,
  getWorkshopsGroups,
} from "framework/workshopsGroups";
import AddWorkshopsGroup from "./partials/AddWorkshopsGroup/AddWorkshopsGroup";
import EditWorkshopsGroup from "./partials/EditWorkshopsGroup/EditWorkshopsGroup";
import DeleteWorkshopsGroup from "./partials/DeleteWorkshopsGroup/DeleteWorkshopsGroup";
import { handleError } from "helpers/utils";
import { IWorkshop } from "models/workshop";
import { getWorkshops } from "framework/workshops";
import { useTranslation } from "react-i18next";
import BlockIcon from "@mui/icons-material/Block";
import { getPermission, getUserData } from "helpers";
import useFetchSection from "hooks/useFetchSection";
import { addInvitation, addInvitationForGroupWorkshops } from "framework/workshopInvitations";
import AddWorkshopInvitations2 from "./partials/AddWorkshop/AddWorkshopInvitations2";
import { IWorkshopInvitationsInput } from "models/workshopInvitations";
import ShowInvitations from "./partials/Show/ShowInvitations";
import { Link } from "react-router-dom";
interface Props {
  feature: Feature;
}

const WorkshopsGroups: React.FC<Props> = ({ feature }) => {
  const [loading2, setLoading2] = useState<boolean>(false);
  const { t, i18n } = useTranslation();

  const [FilterWorkshopsGroups, setFilterWorkshopsGroups] = useState<
    IWorkshopsGroup[]
  >([] as IWorkshopsGroup[]);
  const [Workshops, setWorkshops] = useState<IWorkshop[]>([] as IWorkshop[]);
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [openAddInvitation, setOpenAddInvitation] = useState<boolean>(false);
  const [openShowInvitation, setOpenShowInvitation] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [selectedWorkshopsGroup, setSelectedWorkshopsGroup] =
    useState<IWorkshopsGroup>({} as IWorkshopsGroup);
  const [inputObj, setInputObj] = useState<IWorkshopsGroupInput>(
    {} as IWorkshopsGroupInput
  );
  const { setAlert } = useAlert();
  const user = getUserData();
  const { colors } = user;
  // eslint-disable-next-line
  const queryClient = new QueryClient();

  const addWorkshopsGroupMutation = useMutation({
    mutationFn: (createInput: IWorkshopsGroupInput) => {
      return addWorkshopsGroup(createInput);
    },
  });

  const editWorkshopsGroupMutation = useMutation({
    mutationFn: (data: { createInput: IWorkshopsGroupInput; id: string }) => {
      return editWorkshopsGroup(data.createInput, selectedWorkshopsGroup.id);
    },
  });

  const deleteWorkshopsGroupMutation = useMutation({
    mutationFn: (code: string) => {
      return deleteWorkshopsGroup(code);
    },
  });

  const notifyAttendeesMutation = useMutation({
    mutationFn: () => {
      return addInvitationForGroupWorkshops();
    },
  });

  const sendInvitationsMutation = useMutation({
    mutationFn: (createInput: any) => {
      return addInvitation({
        ...createInput,
      });
    },
  });

  const handleNotifyAttendees = async () => {
    try {
      setLoading2(true);
      await notifyAttendeesMutation.mutateAsync();
      setAlert({
        open: true,
        message: "Attendees were notified successfuly",
        type: "success",
      });
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };



  const fetchWorkshops = useCallback(async () => {
    setLoading2(true);
    try {
      let response: any = await queryClient.fetchQuery("get Workshops", () =>
        getWorkshops()
      );
      setWorkshops(response);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
    // eslint-disable-next-line
  }, [queryClient, setAlert]);
  // get Venue data
  useEffect(() => {
    fetchWorkshops();
    // eslint-disable-next-line
  }, []);

  const {
    loading,
    permission,
    revalidate,
  } = useFetchSection({
    fetchData: () => getWorkshopsGroups(),
    fetchPermission: () => getPermission(14),
    section: "Workshops Groups",
    onSuccess: (data) => setFilterWorkshopsGroups(data),
  });

  if (loading)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  const handleChange = (name: string, value: any) => {
    if (name === "workshops") {
      let workshops = value.map((workshop: any) => workshop.value).join(";");
      setInputObj((prev) => ({
        ...prev,
        [name]: workshops,
      }));
    } else {
      setInputObj((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleOpenAdd = () => setOpenAdd(true);
  const handleCloseAdd = () => setOpenAdd(false);

  const handleOpenEdit = (WorkshopsGroup: IWorkshopsGroup) => {
    setSelectedWorkshopsGroup(WorkshopsGroup);
    setOpenEdit(true);
  };
  const handleCloseEdit = () => {
    setOpenEdit(false);
    setSelectedWorkshopsGroup({} as IWorkshopsGroup);
  };

  const handleOpenDelete = (WorkshopsGroup: IWorkshopsGroup) => {
    setSelectedWorkshopsGroup(WorkshopsGroup);
    setOpenDelete(true);
  };

  const handleOpenAddInvitation = (WorkshopsGroup: IWorkshopsGroup) => {
    setSelectedWorkshopsGroup(WorkshopsGroup);
    setOpenAddInvitation(true);
  };

  const handleOpenShowInvitation = (WorkshopsGroup: IWorkshopsGroup) => {
    setSelectedWorkshopsGroup(WorkshopsGroup);
    setOpenShowInvitation(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setSelectedWorkshopsGroup({} as IWorkshopsGroup);
  };

  const handleCloseAddInvitation = () => {
    setOpenAddInvitation(false);
    setSelectedWorkshopsGroup({} as IWorkshopsGroup);
  };


  const handleCloseShowInvitation = () => {
    setOpenShowInvitation(false);
    setSelectedWorkshopsGroup({} as IWorkshopsGroup);
  };

  const handleSendInvitations = async (selectedEmployees: string) => {
    if (!selectedEmployees)
      return setAlert({
        open: true,
        message: "Please select at least one employee",
        type: "error",
      });
    try {
      setLoading2(true);
      await sendInvitationsMutation.mutateAsync({ workshopGroupId: selectedWorkshopsGroup.id, employeeIds: selectedEmployees });
      setAlert({
        open: true,
        message: "Workshop Group invitations sent successfully",
        type: "success",
      });
      revalidate();
      handleCloseAddInvitation();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const getBodyCells = (row: IWorkshopsGroup) => {
    let cells = Object.keys(row).map((key: string, i: number) => {
      if (key === "workshopTitle")
        return {
          id: "workshopTitle",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.workshopTitle}</Typography>
            </TableCell>
          ),
        };
      if (key === "providers")
        return {
          id: "providers",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.providers}</Typography>
            </TableCell>
          ),
        };
      if (key === "createdOn")
        return {
          id: "createdOn",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {row.createdOn.toString()}
              </Typography>
            </TableCell>
          ),
        };

      if (key === "numberOfWorkshops")
        return {
          id: "numberOfWorkshops",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {row.workshopIds.split(";").length}
              </Typography>
            </TableCell>
          ),
        };

      if (key === "id")
        return {
          id: "actions",
          element: (
            <TableCell align={"left"} key={i}>
              <Stack
                direction={"row"}
                alignContent={"center"}
                spacing={1}
                justifyContent={i18n.language === "en" ? "start" : "end"}
              >
                {permission?.hasModifyPermission && (
                  <Tooltip title={t('T_edit')}>
                    <IconButton
                      size={"small"}
                      color={"primary"}
                      onClick={() => handleOpenEdit(row)}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                )}

                {permission?.hasDeletePermission && (
                  <Tooltip title={t("T_delete")}>
                    <IconButton
                      size={"small"}
                      sx={{
                        color: colors.deleteIconColor
                          ? colors.deleteIconColor
                          : "red",
                        "&:hover": { color: colors?.deleteIconHoverColor },
                      }}
                      onClick={() => handleOpenDelete(row)}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                )}
                {/* <Tooltip title={t("T_add_invitation")}>
                    <IconButton
                      size={"small"}
                      sx={{
                        color: "green",
                        "&:hover": { color: colors?.deleteIconHoverColor },
                      }}
                      onClick={() => handleOpenAddInvitation(row)}
                    >
                      <Add />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title={t("T_workshop_invitations_list")}>
                  <Link to={`${row.id}`}>
                    <IconButton
                      size={"small"}
                      sx={{
                        color: "orange",
                        "&:hover": { color: colors?.deleteIconHoverColor },
                      }}
                      //onClick={() => handleOpenShowInvitation(row)}
                    >
                      <RemoveRedEye />
                    </IconButton>
                    </Link>
                  </Tooltip> */}
                <Tooltip title={t("T_workshop_invitations_list")}>
                  <Link to={`${row.id}`}>
                    <IconButton
                      size={"small"}
                      sx={{
                        color: "orange",
                        "&:hover": { color: colors?.deleteIconHoverColor },
                      }}
                    //onClick={() => handleOpenShowInvitation(row)}
                    >
                      <RemoveRedEye />
                    </IconButton>
                  </Link>
                </Tooltip>
              </Stack>
            </TableCell>
          ),
        };
      else return <></>;
    });

    return cells;
  };

  const handleAdd = async () => {
    if (inputObj.workshops.length < 2)
      return setAlert({
        open: true,
        message: "Choose at least 2 workshops",
        type: "error",
      });
    try {
      setLoading2(true);
      await addWorkshopsGroupMutation.mutateAsync(inputObj);
      setAlert({
        open: true,
        message: "Workshops group added successfully",
        type: "success",
      });
      revalidate();
      handleCloseAdd();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleEdit = async () => {
    if (inputObj.workshops.length < 2)
      return setAlert({
        open: true,
        message: "No changes made",
        type: "error",
      });
    try {
      setLoading2(true);
      let createInput: IWorkshopsGroupInput = {
        workshops:
          inputObj.workshops ||
          selectedWorkshopsGroup.workshopIds
            .split(";")
            .map((workshop: any) => workshop.value)
            .join(";"),
      };
      await editWorkshopsGroupMutation.mutateAsync({
        createInput,
        id: selectedWorkshopsGroup.id,
      });
      setAlert({
        open: true,
        message: "Workshop updated successfully",
        type: "success",
      });
      revalidate();
      handleCloseEdit();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading2(true);
      await deleteWorkshopsGroupMutation.mutateAsync(selectedWorkshopsGroup.id);
      setAlert({
        open: true,
        message: "Workshop deleted successfully",
        type: "success",
      });
      revalidate();
      handleCloseDelete();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  return (
    <>
      <Stack spacing={2} p={2}>
        <Grid
          container
          justifyContent={"space-between"}
          alignItems="center"
          sx={{ mb: 4 }}
        >
          <Typography
            variant="h3"
            color={colors?.labelColor ? colors?.labelColor : "header.primary"}
          >
            {t(feature.name)}
          </Typography>
          <div style={{ display: 'flex', gap: 10 }}>
            <Stack direction={"row"} spacing={1}>
              {permission?.hasModifyPermission && (
                <Tooltip title={t('T_add')}>
                  <IconButton
                    sx={{
                      color: colors.addUpdateIconColor
                        ? colors.addUpdateIconColor
                        : "red",
                      "&:hover": { color: colors?.addUpdateIconHoverColor },
                      height: 40,
                      width: 40,
                      transition: "0.5s",
                    }}
                    onClick={handleOpenAdd}
                  >
                    <Add />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
            <Button
              variant="contained"
              onClick={handleNotifyAttendees}
              style={{ borderRadius: 0, color: "white" }}
            >
              {t('T_send_notifications')}
            </Button>
          </div>
        </Grid>

        {permission?.hasReadPermission! && FilterWorkshopsGroups.length > 0 && (
          <List
            headCells={WorkshopCells}
            data={FilterWorkshopsGroups}
            getBodyCells={getBodyCells}
            feature={feature}
            canEdit={false}
          />
        )}
        {(!FilterWorkshopsGroups || FilterWorkshopsGroups.length === 0) &&
          permission?.hasReadPermission! && (
            <EmptyContent
              icon={<BlindsClosedIcon />}
              header="No Workshops Groups Found"
              message=""
            />
          )}

        {!permission?.hasReadPermission && (
          <EmptyContent
            icon={<BlockIcon sx={{ fontSize: "3.5rem " }} />}
            header={t("T_no_permissions")}
            message={t("T_no_permissions_message")}
          />
        )}
      </Stack>

      {openAdd && (
        <AddWorkshopsGroup
          open={openAdd}
          loading={loading2}
          workshops={Workshops}
          onClose={handleCloseAdd}
          onSubmit={handleAdd}
          onChanges={handleChange}
        />
      )}
      {openEdit && (
        <EditWorkshopsGroup
          open={openEdit}
          loading={loading2}
          workshops={Workshops}
          workshopsGroup={selectedWorkshopsGroup}
          onClose={handleCloseEdit}
          onSubmit={handleEdit}
          onChanges={handleChange}
        />
      )}
      {openDelete && (
        <DeleteWorkshopsGroup
          open={openDelete}
          loading={loading2}
          onClose={handleCloseDelete}
          onSubmit={handleDelete}
        />
      )}

      {openAddInvitation && (
        <AddWorkshopInvitations2
          feature={feature}
          open={openAddInvitation}
          loading={loading2}
          id={selectedWorkshopsGroup!.id}
          onClose={handleCloseAddInvitation}
          onSubmit={handleSendInvitations}
        />
      )}


      {openShowInvitation && (
        <ShowInvitations
          feature={feature}
          open={openShowInvitation}
          id={selectedWorkshopsGroup!.id}
          onClose={handleCloseShowInvitation}
        />
      )}

    </>
  );
};

export default WorkshopsGroups;
